import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import { PhotoPosition, PlantingSiteDetailDto } from '../../models/trofaco/plantingSiteDetailDto';
import { filter, map } from 'rxjs/operators';
import { PhotoCollectionDto } from '../../models/trofaco/photoCollectionDto';
import { PhotoDto } from '../../models/trofaco/photoDto';
import { VideoDto } from '../../models/trofaco/videoDto';
import { PlantingSiteAreaDto } from 'src/app/models/trofaco/plantingSiteAreaDto';
import { LatLngBoundsLiteral } from '@agm/core';

@Injectable()
export class FrontendRouteService {

    constructor(private route: ActivatedRoute, private router: Router) {
    }

    get currentSite$(): Observable<PlantingSiteDetailDto> {
        return this.route.data.pipe(map(data => data.plantingSite));
    }

    get showInfo$(): boolean {
        return this.route.snapshot.paramMap.get('info').trim() == "true";
    }

    get showMap$(): boolean {
        return this.route.snapshot.paramMap.get('map').trim() == "true";
    }

    get showTimeline$(): boolean {
        return this.route.snapshot.paramMap.get('timeline').trim() == "true";
    }

    get showTree$(): boolean {
        return this.route.snapshot.paramMap.get('tree').trim() == "true";
    }

    get currentPlantingSiteArea$(): Observable<PlantingSiteAreaDto> {
        let areaId$ = this.route.paramMap.pipe(map(map => map.get('areaId')));
        return combineLatest([this.currentSite$, areaId$])
            .pipe(
                map(([site, areaId]) => {
                    return site.areas.find(c => c.id == areaId);
                }),
                filter(area => !!area)
            );
    }

    get currentCollection$(): Observable<PhotoCollectionDto> {
        let collectionId$ = this.route.paramMap.pipe(map(map => map.get('collectionId')));
        return combineLatest([this.currentSite$, collectionId$])
            .pipe(
                map(([site, collectionId]) => {
                    return site.collections.find(c => c.id == collectionId);
                }),
                filter(collection => !!collection)
            );
    }

    get currentVideo$(): Observable<VideoDto> {
        let videoId$ = this.route.paramMap.pipe(map(map => map.get('collectionId')));
        return combineLatest([this.currentPlantingSiteArea$, videoId$])
            .pipe(
                map(([siteArea, videoId]) => {
                    return siteArea.videos.find(c => c.id == videoId);
                }),
                filter(video => !!video)
            );
    }

    get currentPhoto$(): Observable<PhotoDto> {
        let photoId$ = this.route.paramMap.pipe(map(map => map.get('photoId')));
        return combineLatest([this.currentCollection$, photoId$])
            .pipe(
                map(([collection, photoId]) => {
                    return collection.photos.find(p => p.id == photoId);
                }),
                filter(photo => !!photo)
            );
    }

    showPhoto(photo: PhotoDto) {
        this.router.navigate(['..', photo.id], {replaceUrl: true, relativeTo: this.route});
    }
}

export class TestFrontendRouteService extends FrontendRouteService {

    constructor() {
        super(null, null);
    }

    defaultSite: PlantingSiteDetailDto = {
        title: 'default site',
        totalTrees: 123,
        region: 'test',
        country: 'home',
        plantingDate: new Date(2015, 5),
        photoPosition: PhotoPosition.middle,
        description: 'hello description',
        landOwner: 'me land owner',
        id: 'abc',
        code: 'ts_123',
        lat: 18.792368370274463,
        lon: 98.99616977552068,
        species: [],
        partners: [],
        areas: [],
        collections: [
            {id: '123', title: 'title', photos: [{id: '123'}]}
        ],
        certificates: []
    };

    get currentSite$(): Observable<PlantingSiteDetailDto> {
        return of(this.defaultSite);
    }

    get currentCollection$(): Observable<PhotoCollectionDto> {
        return of(this.defaultSite.collections[0]);
    }

    get currentPhoto$(): Observable<PhotoDto> {
        return of(this.defaultSite.collections[0].photos[0]);
    }
}
